import React, { useState, useEffect } from 'react';
import { QuizQuestion } from '../types';
import { quickQuizQuestions, comprehensiveQuizQuestions } from '../data/quiz';
import { ChevronLeft } from 'lucide-react';
import { useTheme, getThemeClasses } from '../context/ThemeContext';

interface QuizSectionProps {
  onComplete: (results: any) => void;
  quizType: 'quick' | 'comprehensive';
  onBack: () => void;
}

export default function QuizSection({ onComplete, quizType, onBack }: QuizSectionProps) {
  const { theme } = useTheme();
  const themeClasses = getThemeClasses(theme);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const questions = quizType === 'quick' ? quickQuizQuestions : comprehensiveQuizQuestions;

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key >= '1' && e.key <= '9') {
        const index = parseInt(e.key) - 1;
        if (index < questions[currentQuestion].options.length) {
          handleAnswer(questions[currentQuestion].options[index].value);
        }
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [currentQuestion, questions]);

  const handleAnswer = (value: string) => {
    setIsTransitioning(true);
    const newAnswers = {
      ...answers,
      [questions[currentQuestion].id]: value,
      quiz_type: quizType // Add quiz type to answers
    };
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(curr => curr + 1);
        setIsTransitioning(false);
      } else {
        setIsCompleting(true);
        setTimeout(() => {
          onComplete(newAnswers);
        }, 500);
      }
    }, 300);
  };

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (isCompleting) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className={`${themeClasses.card} rounded-xl shadow-lg p-8 text-center`}>
          <div className="flex flex-col items-center space-y-4">
            <div className={`w-16 h-16 border-4 ${themeClasses.primary} border-t-transparent rounded-full animate-spin`} />
            <h3 className={`text-xl font-semibold ${themeClasses.text}`}>
              Finding your perfect book match...
            </h3>
            <p className={themeClasses.textSecondary}>
              Analyzing your preferences and curating personalized recommendations
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <button
        onClick={onBack}
        className={`mb-6 ${themeClasses.icon} hover:opacity-80 flex items-center group`}
      >
        <ChevronLeft className="h-4 w-4 mr-1 group-hover:-translate-x-1 transition-transform" />
        Back to quiz selection
      </button>
      
      <div className={`${themeClasses.card} rounded-xl shadow-lg p-6`}>
        <div className="mb-8">
          <div className="mb-4">
            <div className={`h-2 w-full ${themeClasses.secondary} rounded-full overflow-hidden`}>
              <div
                className={`h-2 ${themeClasses.primary} rounded-full transition-all duration-500 ease-out`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className={`mt-2 text-sm ${themeClasses.textSecondary} text-right`}>
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>
          <h3 className={`text-2xl font-semibold ${themeClasses.text} mb-4 transition-opacity duration-300
                       ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            {question.question}
          </h3>
        </div>
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity duration-300
                      ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          {question.options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => handleAnswer(option.value)}
              className={`p-4 text-lg border-2 ${themeClasses.border} rounded-lg hover:${themeClasses.highlight} 
                       transition-all duration-200 text-left group relative ${themeClasses.text}`}
            >
              <span className={`absolute top-2 right-2 text-xs ${themeClasses.textSecondary}`}>
                {index + 1}
              </span>
              {option.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}